import {
  defaultThrowOnError,
  QueryFactory,
  SubtenantDto,
} from '@lmt/shared-types';
import { useMemo } from 'react';
import { emptyArray } from '../../helpers/common';

export function useSubtenants(): SubtenantDto[] {
  return (
    QueryFactory.TenantQuery.useGetSubtenantsQuery(
      {},
      { throwOnError: defaultThrowOnError },
    ).data?.data ?? emptyArray()
  );
}

export function useSubtenantsDict(options?: {
  excludeCodeIfSame: boolean;
}): Record<number, SubtenantDto>;

export function useSubtenantsDict(options?: { excludeCodeIfSame: boolean }) {
  const subtenants = useSubtenants();
  const excludeCodeIfSame = options?.excludeCodeIfSame ?? true;

  return useMemo(() => {
    const areAllCodesEqual = subtenants.every(
      (s) => s.code === subtenants[0]?.code,
    );
    const dict: Record<number, SubtenantDto> = {};
    subtenants.forEach((s) => {
      dict[s.id] = { ...s };
      if (excludeCodeIfSame && areAllCodesEqual) {
        dict[s.id].code = '';
      }
    });
    return dict;
  }, [subtenants, excludeCodeIfSame]);
}

export function useSubtenant(
  subtenantId: number,
  options?: { excludeCodeIfSame?: boolean },
): SubtenantDto;
export function useSubtenant(
  subtenantId: number | undefined,
): SubtenantDto | undefined;
export function useSubtenant(
  subtenantId: number | undefined,
  options?: { excludeCodeIfSame?: boolean },
): SubtenantDto | undefined {
  const excludeCodeIfSame = options?.excludeCodeIfSame ?? true;
  const subtenantsDict = useSubtenantsDict({
    excludeCodeIfSame,
  });
  return subtenantId ? subtenantsDict[subtenantId!] : undefined!;
}

export function useSelectedSubtenants(
  subtenantIds: (number | null)[] | undefined,
): SubtenantDto[] {
  const subtenants: SubtenantDto[] = useSubtenants();
  const subtenantsDict = useSubtenantsDict();
  return useMemo<SubtenantDto[]>(
    () =>
      subtenantIds?.length
        ? subtenantIds
            .filter((subtenantId) => subtenantId && subtenantsDict[subtenantId])
            .map((subtenantId) => subtenantsDict[subtenantId!])
        : subtenants,
    [subtenantIds, subtenantsDict],
  );
}
